/**
 * Optimized API Services with Intelligent Caching
 * 
 * These services automatically apply performance optimizations
 * based on usage patterns and data characteristics
 */

import { enhancedApiClient, EnhancedRequestConfig } from './enhanced-client';
import { cacheConfigs, cacheManager } from './cache-manager';
import type { 
  Condominium, 
  User, 
  PaginatedResponse,
  PreventiveMaintenance,
  CreatePreventiveMaintenanceDto,
  UpdatePreventiveMaintenanceDto
} from '@/types';

// Types for optimized services
export interface OptimizedQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

export interface CacheSettings {
  enabled: boolean;
  ttl: number;
  tags: string[];
  strategy: 'memory' | 'storage' | 'hybrid';
}

// Base optimized service class
abstract class OptimizedBaseService {
  protected baseUrl: string;
  protected defaultCacheSettings: CacheSettings;

  constructor(baseUrl: string, defaultCacheSettings: CacheSettings) {
    this.baseUrl = baseUrl;
    this.defaultCacheSettings = defaultCacheSettings;
  }

  protected getConfig(overrides: Partial<EnhancedRequestConfig> = {}): EnhancedRequestConfig {
    return {
      enableCaching: this.defaultCacheSettings.enabled,
      cacheTTL: this.defaultCacheSettings.ttl,
      cacheTags: this.defaultCacheSettings.tags,
      cacheStrategy: this.defaultCacheSettings.strategy,
      enableMetrics: true,
      enableOptimization: true,
      retryConfig: 'standard',
      ...overrides,
    };
  }

  protected generateCacheKey(method: string, endpoint: string, params?: object): string {
    const establishmentId = localStorage.getItem('mep_current_establishment') || '';
    const normalizedParams = params ? JSON.stringify(params, Object.keys(params).sort()) : '';
    return `${method}:${this.baseUrl}${endpoint}:${normalizedParams}:${establishmentId}`;
  }

  protected async invalidateCache(patterns: string[]): Promise<void> {
    for (const pattern of patterns) {
      await enhancedApiClient.clearCache(new RegExp(pattern));
    }
  }
}

// Optimized Establishments Service
export class OptimizedEstablishmentsService extends OptimizedBaseService {
  constructor() {
    super('/establishments', {
      enabled: true,
      ttl: cacheConfigs.establishmentSpecific.ttl,
      tags: ['establishments', 'reference'],
      strategy: 'hybrid',
    });
  }

  /**
   * Get paginated establishments with intelligent caching
   */
  async getEstablishments(params: OptimizedQueryParams = {}): Promise<PaginatedResponse<Condominium>> {
    const cacheKey = this.generateCacheKey('GET', '', params);
    
    // Use longer cache for reference-like queries (no search, standard pagination)
    const isReferenceQuery = !params.search && (params.page || 1) <= 3 && (params.limit || 10) <= 20;
    const cacheTTL = isReferenceQuery ? cacheConfigs.longTerm.ttl : cacheConfigs.establishmentSpecific.ttl;

    const response = await enhancedApiClient.get<PaginatedResponse<Condominium>>(
      this.baseUrl,
      this.getConfig({
        params,
        cacheKey,
        cacheTTL,
        debounceDelay: params.search ? 300 : 0, // Debounce search queries
      })
    );

    // Backend now provides data in the correct format
    return response.data;
  }

  /**
   * Get single establishment with aggressive caching
   */
  async getEstablishment(id: string): Promise<Condominium> {
    const cacheKey = this.generateCacheKey('GET', `/${id}`);
    
    const response = await enhancedApiClient.get<Condominium>(
      `${this.baseUrl}/${id}`,
      this.getConfig({
        cacheKey,
        cacheTTL: cacheConfigs.longTerm.ttl, // Establishments don't change often
        cacheTags: [...this.defaultCacheSettings.tags, `establishment:${id}`],
      })
    );

    // Backend now provides data in the correct format
    return response.data;
  }

  /**
   * Create establishment with cache invalidation
   */
  async createEstablishment(data: Partial<Condominium>): Promise<Condominium> {
    const response = await enhancedApiClient.post<Condominium>(
      this.baseUrl,
      data,
      this.getConfig({
        enableCaching: false, // Don't cache POST requests
        retryConfig: 'critical', // Use critical retry for creation
      })
    );

    // Invalidate list caches
    await this.invalidateCache([
      `GET:${this.baseUrl}:.*`,
      'establishments',
    ]);

    // Backend now provides data in the correct format
    return response.data;
  }

  /**
   * Update establishment with targeted cache invalidation
   */
  async updateEstablishment(id: string, data: Partial<Condominium>): Promise<Condominium> {
    const response = await enhancedApiClient.patch<Condominium>(
      `${this.baseUrl}/${id}`,
      data,
      this.getConfig({
        enableCaching: false,
        retryConfig: 'critical',
      })
    );

    // Invalidate specific establishment and list caches
    await this.invalidateCache([
      `GET:${this.baseUrl}/${id}:.*`,
      `GET:${this.baseUrl}:.*`,
      `establishment:${id}`,
      'establishments',
    ]);

    // Backend now provides data in the correct format
    return response.data;
  }

  /**
   * Get multiple establishments in batch
   */
  async getEstablishmentsBatch(ids: string[]): Promise<Condominium[]> {
    // Check cache for individual items first
    const cachedResults = new Map<string, Condominium>();
    const uncachedIds: string[] = [];

    for (const id of ids) {
      const cacheKey = this.generateCacheKey('GET', `/${id}`);
      try {
        const cached = await cacheManager.get<Condominium>(cacheKey);
        if (cached) {
          cachedResults.set(id, cached);
        } else {
          uncachedIds.push(id);
        }
      } catch {
        uncachedIds.push(id);
      }
    }

    // Fetch uncached items
    const results: Condominium[] = [];
    
    if (uncachedIds.length > 0) {
      // Use batch request for remaining items
      const batchRequests = uncachedIds.map(id => ({
        method: 'GET' as const,
        url: `${this.baseUrl}/${id}`,
        config: this.getConfig({
          cacheKey: this.generateCacheKey('GET', `/${id}`),
          cacheTTL: cacheConfigs.longTerm.ttl,
        }),
      }));

      const batchResponses = await enhancedApiClient.batch<Condominium>(batchRequests);
      
      for (let i = 0; i < uncachedIds.length; i++) {
        const uncachedId = uncachedIds[i];
        const batchResponse = batchResponses[i];
        if (!uncachedId || !batchResponse) {
          continue;
        }

        const establishment = batchResponse.data;
        results.push(establishment);
        cachedResults.set(uncachedId, establishment);
      }
    }

    // Return results in original order
    return ids
      .map((id) => cachedResults.get(id))
      .filter((item): item is Condominium => item !== undefined);
  }

  // Transformation methods removed - backend provides data in correct format
}

// Optimized Users Service
export class OptimizedUsersService extends OptimizedBaseService {
  constructor() {
    super('/users', {
      enabled: true,
      ttl: cacheConfigs.userSpecific.ttl,
      tags: ['users'],
      strategy: 'hybrid',
    });
  }

  /**
   * Get paginated users with search optimization
   */
  async getUsers(params: OptimizedQueryParams = {}): Promise<PaginatedResponse<User>> {
    const cacheKey = this.generateCacheKey('GET', '', params);
    
    const response = await enhancedApiClient.get<PaginatedResponse<User>>(
      this.baseUrl,
      this.getConfig({
        params,
        cacheKey,
        debounceDelay: params.search ? 500 : 0, // Longer debounce for user search
        cacheTTL: params.search ? cacheConfigs.searchResults.ttl : cacheConfigs.userSpecific.ttl,
      })
    );

    // Backend now provides data in the correct format
    return response.data;
  }

  /**
   * Get user by ID with long-term caching
   */
  async getUserById(id: string): Promise<User> {
    const cacheKey = this.generateCacheKey('GET', `/${id}`);
    
    const response = await enhancedApiClient.get<User>(
      `${this.baseUrl}/${id}`,
      this.getConfig({
        cacheKey,
        cacheTags: [...this.defaultCacheSettings.tags, `user:${id}`],
      })
    );

    // Backend now provides data in the correct format
    return response.data;
  }

  /**
   * Get current user with short-term caching
   */
  async getCurrentUser(): Promise<User> {
    const cacheKey = this.generateCacheKey('GET', '/me');
    
    // Use /auth/me endpoint instead of /users/me
    const response = await enhancedApiClient.get<User>(
      '/auth/me',
      this.getConfig({
        cacheKey,
        cacheTTL: 2 * 60 * 1000, // 2 minutes for current user data
        cacheTags: ['user-specific', 'current-user'],
      })
    );

    // Backend now provides data in the correct format
    return response.data;
  }

  /**
   * Get user establishments with caching
   */
  async getUserEstablishments(userId?: string): Promise<Array<{
    id: string;
    name: string;
    roles: string[];
  }>> {
    const endpoint = userId ? `/${userId}/establishments` : '/me/establishments';
    const cacheKey = this.generateCacheKey('GET', endpoint);
    
    const response = await enhancedApiClient.get<Array<{
      id: string;
      name: string;
      roles: string[];
    }>>(
      `${this.baseUrl}${endpoint}`,
      this.getConfig({
        cacheKey,
        cacheTTL: cacheConfigs.establishmentSpecific.ttl,
        cacheTags: ['user-establishments', userId ? `user:${userId}` : 'current-user'],
      })
    );

    return response.data;
  }

  /**
   * Create user with cache invalidation
   */
  async createUser(userData: Partial<User>): Promise<User> {
    const response = await enhancedApiClient.post<User>(
      this.baseUrl,
      userData,
      this.getConfig({
        enableCaching: false,
        retryConfig: 'critical',
      })
    );

    // Invalidate user list caches
    await this.invalidateCache([
      `GET:${this.baseUrl}:.*`,
      'users',
    ]);

    // Backend now provides data in the correct format
    return response.data;
  }

  /**
   * Update user with targeted invalidation
   */
  async updateUser(id: string, userData: Partial<User>): Promise<User> {
    const response = await enhancedApiClient.patch<User>(
      `${this.baseUrl}/${id}`,
      userData,
      this.getConfig({
        enableCaching: false,
        retryConfig: 'critical',
      })
    );

    // Invalidate specific user and list caches
    await this.invalidateCache([
      `GET:${this.baseUrl}/${id}:.*`,
      `GET:${this.baseUrl}:.*`,
      `user:${id}`,
      'users',
      'current-user', // In case this is the current user
    ]);

    // Backend now provides data in the correct format
    return response.data;
  }

  /**
   * Check email availability with debouncing
   */
  async checkEmailAvailability(email: string, userId?: string): Promise<boolean> {
    const params = { email, userId };
    const cacheKey = this.generateCacheKey('GET', '/check-email', params);
    
    const response = await enhancedApiClient.get<{ available: boolean }>(
      `${this.baseUrl}/check-email`,
      this.getConfig({
        params,
        cacheKey,
        cacheTTL: 30 * 1000, // 30 seconds cache for email checks
        debounceDelay: 300,
        cacheTags: ['email-check'],
      })
    );

    return response.data.available;
  }

  // Transformation methods removed - backend provides data in correct format
}

// Optimized Maintenance Service
export class OptimizedMaintenanceService extends OptimizedBaseService {
  constructor() {
    super('/preventive-maintenances', {
      enabled: true,
      ttl: cacheConfigs.searchResults.ttl,
      tags: ['maintenance'],
      strategy: 'hybrid',
    });
  }

  /**
   * Get maintenance records with smart caching
   */
  async getMaintenanceRecords(params: OptimizedQueryParams = {}): Promise<PaginatedResponse<PreventiveMaintenance>> {
    const cacheKey = this.generateCacheKey('GET', '', params);
    
    // Use different cache strategies based on query type
    let cacheTTL = cacheConfigs.searchResults.ttl;
    if (!params.search && (params.page || 1) === 1) {
      cacheTTL = cacheConfigs.establishmentSpecific.ttl; // First page cached longer
    }

    const response = await enhancedApiClient.get<PaginatedResponse<PreventiveMaintenance>>(
      this.baseUrl,
      this.getConfig({
        params,
        cacheKey,
        cacheTTL,
        debounceDelay: params.search ? 400 : 0,
      })
    );

    return response.data;
  }

  /**
   * Get maintenance by ID with caching
   */
  async getMaintenanceById(id: string): Promise<PreventiveMaintenance> {
    const cacheKey = this.generateCacheKey('GET', `/${id}`);
    
    const response = await enhancedApiClient.get<PreventiveMaintenance>(
      `${this.baseUrl}/${id}`,
      this.getConfig({
        cacheKey,
        cacheTags: [...this.defaultCacheSettings.tags, `maintenance:${id}`],
      })
    );

    return response.data;
  }

  /**
   * Create maintenance record
   */
  async createMaintenance(data: CreatePreventiveMaintenanceDto): Promise<PreventiveMaintenance> {
    const response = await enhancedApiClient.post<PreventiveMaintenance>(
      this.baseUrl,
      data,
      this.getConfig({
        enableCaching: false,
        retryConfig: 'standard',
      })
    );

    // Invalidate maintenance caches
    await this.invalidateCache([
      `GET:${this.baseUrl}:.*`,
      'maintenance',
    ]);

    return response.data;
  }

  /**
   * Update maintenance record
   */
  async updateMaintenance(id: string, data: UpdatePreventiveMaintenanceDto): Promise<PreventiveMaintenance> {
    const response = await enhancedApiClient.patch<PreventiveMaintenance>(
      `${this.baseUrl}/${id}`,
      data,
      this.getConfig({
        enableCaching: false,
        retryConfig: 'standard',
      })
    );

    // Invalidate specific maintenance and list caches
    await this.invalidateCache([
      `GET:${this.baseUrl}/${id}:.*`,
      `GET:${this.baseUrl}:.*`,
      `maintenance:${id}`,
      'maintenance',
    ]);

    return response.data;
  }
}

// Export service instances
export const optimizedEstablishmentsService = new OptimizedEstablishmentsService();
export const optimizedUsersService = new OptimizedUsersService();
export const optimizedMaintenanceService = new OptimizedMaintenanceService();

// Export utility functions for performance monitoring
export const performanceUtils = {
  /**
   * Prefetch commonly accessed data
   */
  async prefetchCommonData(): Promise<void> {
    try {
      // Prefetch current user
      enhancedApiClient.prefetch('GET', '/auth/me', undefined, {
        ttl: cacheConfigs.userSpecific.ttl,
        tags: ['current-user'],
        priority: 'high',
      });

      // Prefetch user establishments
      enhancedApiClient.prefetch('GET', '/users/me/establishments', undefined, {
        ttl: cacheConfigs.establishmentSpecific.ttl,
        tags: ['user-establishments'],
        priority: 'high',
      });

      // Prefetch first page of common resources
      enhancedApiClient.prefetch('GET', '/establishments', { page: 1, limit: 10 }, {
        ttl: cacheConfigs.establishmentSpecific.ttl,
        tags: ['establishments'],
        priority: 'medium',
      });
    } catch (error) {
      console.warn('Prefetch failed:', error);
    }
  },

  /**
   * Get performance summary
   */
  getPerformanceSummary() {
    try {
      const metrics = enhancedApiClient.getPerformanceMetrics();
      const cacheStats = enhancedApiClient.getCacheStatistics();
      
      return {
        ...metrics,
        cache: cacheStats,
        recommendations: this.generatePerformanceRecommendations(metrics, cacheStats),
      };
    } catch (error) {
      console.warn('Performance summary unavailable:', error);
      // Return fallback data
      const fallbackMetrics = {
        summary: { totalEndpoints: 0, slowEndpoints: 0, highErrorRateEndpoints: 0, avgResponseTime: 0, totalRequests: 0, cacheHitRate: 0 },
        slowEndpoints: [],
        highErrorRateEndpoints: [],
        recentAlerts: [],
        recommendations: []
      };
      const fallbackCacheStats = { hits: 0, misses: 0, sets: 0, deletes: 0, evictions: 0, hitRate: 0, avgResponseTime: 0, totalSize: 0, entryCount: 0 };
      
      return {
        ...fallbackMetrics,
        cache: fallbackCacheStats,
        recommendations: ['Performance monitoring temporarily unavailable'],
      };
    }
  },

  /**
   * Generate performance recommendations
   */
  generatePerformanceRecommendations(performanceMetrics: {
    summary: {
      slowEndpoints: number;
      avgResponseTime: number;
    };
  }, cacheStats: {
    hitRate: number;
    entryCount: number;
  }): string[] {
    const recommendations: string[] = [];

    if (cacheStats.hitRate < 0.5) {
      recommendations.push('Cache hit rate is low. Consider enabling caching for more endpoints.');
    }

    if (performanceMetrics.summary.slowEndpoints > 3) {
      recommendations.push('Multiple slow endpoints detected. Review and optimize slow queries.');
    }

    if (performanceMetrics.summary.avgResponseTime > 1000) {
      recommendations.push('Average response time is high. Consider implementing more aggressive caching.');
    }

    if (cacheStats.entryCount > 800) {
      recommendations.push('Cache size is large. Consider reducing cache TTL for some endpoints.');
    }

    return recommendations;
  },

  /**
   * Clear all optimized service caches
   */
  async clearAllCaches(): Promise<void> {
    await enhancedApiClient.clearCache();
  },

  /**
   * Clear specific service cache
   */
  async clearServiceCache(serviceName: 'establishments' | 'users' | 'maintenance'): Promise<void> {
    await enhancedApiClient.clearCache(serviceName);
  },
};

export default {
  establishments: optimizedEstablishmentsService,
  users: optimizedUsersService,
  maintenance: optimizedMaintenanceService,
  utils: performanceUtils,
};
